import {
  addArrayFiltersToParams,
  addStringFiltersToParams,
  cleanFilterParams,
} from '@/utils/convertFilters';
import { api } from './api';

export interface SubscriptionsFilterParams {
  status?: string[];
  payment_methods?: string[];
  products?: string[];
  offers?: string[];
  affiliates?: string[];
  coproducers?: string[];
  src?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export type SubscriptionDateAxis = 'started' | 'canceled' | 'billing';

function appendDateParams(
  urlParams: URLSearchParams,
  startDate?: string,
  endDate?: string,
  dateAxis?: SubscriptionDateAxis,
) {
  if (!startDate || !endDate) {
    return;
  }

  if (dateAxis === 'canceled') {
    urlParams.append('canceled_start_date', startDate);
    urlParams.append('canceled_end_date', endDate);
  } else if (dateAxis === 'billing') {
    urlParams.append('billing_start_date', startDate);
    urlParams.append('billing_end_date', endDate);
  } else {
    urlParams.append('start_date', startDate);
    urlParams.append('end_date', endDate);
  }
}

function appendFilterParams(
  urlParams: URLSearchParams,
  filterParams?: SubscriptionsFilterParams,
  q?: string,
) {
  const cleaned = cleanFilterParams(filterParams);
  addArrayFiltersToParams(urlParams, cleaned);
  addStringFiltersToParams(urlParams, cleaned);

  if (q && q.length > 1) {
    urlParams.append('q', q);
  }
}

export interface SubscriptionListItem {
  id: string;
  public_id: string;
  status: string;
  amount_formatted: string;
  product_name?: string;
  offer_name?: string;
  offer_type?: string | null;
  customer_name?: string;
  customer_email?: string;
  order_id?: string | null;
  payment_method?: { type?: string; brand?: string; last4?: string };
  started_at_formatted?: string;
  next_billing_at_formatted?: string;
  interval_label_key?: string;
  cancel_requested_at?: string | null;
}

export interface SubscriptionsListResponse {
  data: SubscriptionListItem[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
  links: {
    next?: string | null;
    prev?: string | null;
  };
}

export interface SubscriptionDetailItem {
  public_id: string;
  status: string;
  amount_formatted: string;
  currency?: string;
  interval_label_key?: string;
  current_cycle?: number;
  max_cycles?: number | null;
  started_at_formatted?: string;
  current_period_start_formatted?: string;
  current_period_end_formatted?: string;
  next_billing_at_formatted?: string;
  in_trial?: boolean;
  trial_mode?: 'free' | 'promotional' | null;
  trial_ends_at_formatted?: string | null;
  next_shipping_estimate_formatted?: string | null;
  cancel_mode?: string | null;
  cancel_requested_at?: string | null;
  cancel_requested_at_formatted?: string | null;
  ends_at_formatted?: string | null;
  payment_method?: { type?: string; brand?: string; last4?: string };
  product_name?: string;
  offer_name?: string;
  customer_name?: string;
  customer_email?: string;
  product_image_url?: string | null;
  order_id?: string | null;
  provider?: string | null;
}

export interface SubscriptionSignupOffer {
  offer_id?: string | null;
  name: string;
  type: string;
  price_formatted: string;
  payment_type?: 'single' | 'recurring' | string | null;
  recurring_interval?: string | null;
  is_current_subscription?: boolean;
  subscription_public_id?: string | null;
  subscription_status?: string | null;
  subscription_current_cycle?: number | null;
  subscription_next_billing_at_formatted?: string | null;
  subscription_amount_formatted?: string | null;
  subscription_currency?: string | null;
  interval_label_key?: string | null;
  product: { name: string; images: Array<{ url: string }> };
}

export interface SubscriptionSignupOrder {
  id: string;
  status: string;
  /** Moeda de cobrança no checkout. */
  currency?: string;
  /** Moeda de liquidação do produtor (pode diferir quando há FX). */
  settlement_currency?: string;
  total_formatted: string;
  created_at_formatted?: string;
  offers?: SubscriptionSignupOffer[];
  customer?: {
    name?: string;
    email?: string;
    phone?: string;
    phone_prefix?: string;
    document?: { document_value?: string };
    country?: { code?: string; name?: string };
  };
  currencyConversion?: {
    from_currency?: string;
    to_currency?: string;
    amount_formatted?: string;
    converted_amount_formatted?: string;
  } | null;
}

export interface SubscriptionDetailResponse {
  subscription: SubscriptionDetailItem;
  signup_order: SubscriptionSignupOrder | null;
  actions: {
    can_cancel: boolean;
    cancel_blocked_reason?: string | null;
  };
}

export interface SubscriptionInvoiceItem {
  public_id: string;
  cycle_number: number;
  status: string;
  amount_formatted: string;
  due_at_formatted?: string | null;
  paid_at_formatted?: string | null;
  mundpay_order_id?: string | null;
  shipping?: {
    amount_formatted?: string | null;
    is_free?: boolean;
  } | null;
  subscription_amount_formatted?: string | null;
}

export interface SubscriptionCycleItem {
  cycle_number: number;
  status: string;
  amount_formatted: string;
  period_start_formatted?: string | null;
  period_end_formatted?: string | null;
  invoice_public_id?: string | null;
}

export interface PaginatedSubscriptionMeta {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export class SubscriptionsService {
  static async getSubscriptions({
    startDate,
    endDate,
    dateAxis,
    filterParams,
    q,
    currency,
    page = 1,
  }: {
    startDate?: string;
    endDate?: string;
    dateAxis?: SubscriptionDateAxis;
    filterParams?: SubscriptionsFilterParams;
    q?: string;
    currency: string;
    page?: number;
  }): Promise<SubscriptionsListResponse> {
    const urlParams = new URLSearchParams();
    appendDateParams(urlParams, startDate, endDate, dateAxis);
    urlParams.append('currency', currency);
    urlParams.append('page', String(page));
    appendFilterParams(urlParams, filterParams, q);

    const response = await api.get<SubscriptionsListResponse>(
      `/v1/subscriptions?${urlParams.toString()}`,
    );

    return response.data;
  }

  static async getReportsByStatus({
    startDate,
    endDate,
    dateAxis,
    filterParams,
    q,
    currency,
  }: {
    startDate?: string;
    endDate?: string;
    dateAxis?: SubscriptionDateAxis;
    filterParams?: SubscriptionsFilterParams;
    q?: string;
    currency: string;
  }) {
    const urlParams = new URLSearchParams();
    appendDateParams(urlParams, startDate, endDate, dateAxis);
    urlParams.append('currency', currency);
    appendFilterParams(urlParams, filterParams, q);

    const response = await api.get(
      `/v1/reports/subscriptions/by-status?${urlParams.toString()}`,
    );

    return response.data;
  }

  static async getTimeDistribution({
    startDate,
    endDate,
    dateAxis,
    filterParams,
    q,
    currency,
  }: {
    startDate?: string;
    endDate?: string;
    dateAxis?: SubscriptionDateAxis;
    filterParams?: SubscriptionsFilterParams;
    q?: string;
    currency: string;
  }) {
    const urlParams = new URLSearchParams();
    appendDateParams(urlParams, startDate, endDate, dateAxis);
    urlParams.append('currency', currency);
    appendFilterParams(urlParams, filterParams, q);

    const response = await api.get(
      `/v1/reports/subscriptions/time-distribution?${urlParams.toString()}`,
    );

    return response.data;
  }

  static async getSubscriptionDetail(
    publicId: string,
  ): Promise<SubscriptionDetailResponse> {
    const response = await api.get<{ data: SubscriptionDetailResponse }>(
      `/v1/subscriptions/${publicId}`,
    );

    return response.data.data;
  }

  static async getSubscriptionInvoices(
    publicId: string,
    page = 1,
    perPage = 10,
  ): Promise<{ data: SubscriptionInvoiceItem[]; meta: PaginatedSubscriptionMeta }> {
    const response = await api.get<{
      data: SubscriptionInvoiceItem[];
      meta: PaginatedSubscriptionMeta;
    }>(`/v1/subscriptions/${publicId}/invoices?page=${page}&per_page=${perPage}`);

    return response.data;
  }

  static async getSubscriptionCycles(
    publicId: string,
    page = 1,
    perPage = 10,
  ): Promise<{ data: SubscriptionCycleItem[]; meta: PaginatedSubscriptionMeta }> {
    const response = await api.get<{
      data: SubscriptionCycleItem[];
      meta: PaginatedSubscriptionMeta;
    }>(`/v1/subscriptions/${publicId}/cycles?page=${page}&per_page=${perPage}`);

    return response.data;
  }

  static async cancelSubscription(publicId: string) {
    const response = await api.delete<{ data: Record<string, unknown> }>(
      `/v1/subscriptions/${publicId}`,
    );

    return response.data.data;
  }
}
